import { interpolate, spring } from "remotion";
import { CARDS_PER_STOP } from "../reelConstants";

/**
 * What a stop's marker writes to the map, in one place.
 *
 * MarkerSequence springs a stop's pin in over its one second and writes every value of it -- the
 * circle, the number, the name, the pulse, the photo fan -- in its render body. Those are Mapbox
 * layers, not React elements, so when the sequence ends they keep whatever they were last given, and
 * the next thing to touch them is PathSequence's `fadeStop` on the first frame of the leg that leaves
 * the stop. `fadeStop` writes opacities only: the stop fades out from the layout MarkerSequence left it
 * in -- the name's offset, the circle's radius, the number's size, the fan's tilt.
 *
 * A render tab that starts after the marker never ran that sequence, so its layers are still in the
 * state they were created in, and the stop fades out from there instead: a name label at another
 * offset, a circle of another size, for the five or so frames the fade takes. FinishedSequences puts
 * the marker's last frame back, and it has to be exactly what MarkerSequence leaves, so both call
 * `writeStopMarker` -- MarkerSequence with the frame it is on, FinishedSequences with the last one.
 * When these were two copies of the same writes, the first change to one of them was going to be a
 * render that differed by tab.
 *
 * Only `remotion`'s pure functions are used here (`spring`, `interpolate`); nothing reads the frame.
 */

export type StopMarkerFrame = {
	/** The stop whose layers these are. */
	index: number;
	/** The frame within the stop's own sequence: 0 on its first. */
	markerFrame: number;
	/** How long the stop lasts, in frames. */
	markerDuration: number;
	fps: number;
	reelData: any[];
	showPhotoCards: boolean;
	cardStagger: number;
	cardStiffness: number;
	scale: number;
};

export const writeStopMarker = (map: any, frame: StopMarkerFrame, { opacities = true }: { opacities?: boolean } = {}) => {
	const { index, markerFrame, markerDuration, fps, reelData, showPhotoCards, cardStagger, cardStiffness, scale } = frame;

	const driver = spring({
		frame: markerFrame,
		fps,
		config: {
			mass: 1,
			stiffness: 300,
		},
	});

	const fadeIn = interpolate(driver, [0, 1], [0, 1], {
		extrapolateRight: "clamp",
	});
	const markerRadius = interpolate(driver, [0, 1], [0, 48 * scale]);
	// This is the value that actually lands -- it is written every frame and overrides whatever the
	// layer was created with, which is why editing the paint block in TikTokVideoVanilla did nothing.
	// The pill is two lines at 46px, so about 142 tall; its bottom edge has to clear the 32px-radius
	// dot, and the top is meant to overlap the card stack above it.
	const destinationNameTranslate = interpolate(driver, [0, 1], [0, (showPhotoCards ? -126 : -88) * scale]);
	const pulseOpacity = interpolate(markerFrame, [0, 4, markerDuration / 1.75], [0, 1, 0], {
		extrapolateRight: "clamp",
	});
	const pulseRadius = interpolate(markerFrame, [0, 8], [0, 100 * scale]);
	const countSize = interpolate(driver, [0, 1], [0, 40 * scale], {
		extrapolateRight: "clamp",
		extrapolateLeft: "clamp",
	});

	// Helper to safely set paint/layout properties only if layer exists
	const safeSetPaint = (layerId: string, prop: string, value: any) => {
		try {
			if (map?.getLayer(layerId)) {
				map.setPaintProperty(layerId, prop, value);
			}
		} catch (e) {
			// Layer doesn't exist, skip silently
		}
	};
	const safeSetLayout = (layerId: string, prop: string, value: any) => {
		try {
			if (map?.getLayer(layerId)) {
				map.setLayoutProperty(layerId, prop, value);
			}
		} catch (e) {
			// Layer doesn't exist, skip silently
		}
	};
	// The opacities are the one part of a stop's state that something else owns once the stop is over --
	// PathSequence fades it on the leg that leaves -- so a caller that is only restoring a stop's layout
	// asks for them to be left out. MarkerSequence itself always writes them.
	const safeSetOpacity = (layerId: string, prop: string, value: any) => {
		if (opacities) safeSetPaint(layerId, prop, value);
	};

	// Hide transport marker from previous PathSequence (it's only relevant during path animation)
	safeSetOpacity("transportMarkerDot", "circle-opacity", 0);
	safeSetOpacity("transportMarkerIcon", "icon-opacity", 0);
	safeSetOpacity("transportProfilePicture", "icon-opacity", 0);

	//Animate marker background
	safeSetOpacity(`marker-${index}`, "circle-opacity", fadeIn);
	safeSetPaint(`marker-${index}`, "circle-radius", markerRadius);

	//Animate marker number
	safeSetOpacity(`countText-${index}`, "text-opacity", fadeIn);
	safeSetOpacity(`countText-${index}`, "icon-opacity", fadeIn);

	if (showPhotoCards) {
		// The fan pops one card at a time.
		//
		// Each card gets its own spring started a few frames later than the last, so they arrive in
		// sequence rather than together. The spread and the tilt come from the card's position in the fan,
		// and the whole fan flips direction on alternate stops so consecutive destinations do not look
		// stamped from the same template.
		const fanDirection = index % 2 === 0 ? 1 : -1;
		// Fan as many cards as this stop has pictures. Animating three regardless left two empty
		// layers spreading and tilting where a stop only had one photograph to show.
		// Same rule the layers were built with: photographs if the trip has any, otherwise the pool.
		const anyPhotos = (reelData || []).some((d: any) => (d?.photos?.length ?? 0) > 0);
		const own = Math.min(CARDS_PER_STOP, reelData?.[index]?.photos?.length ?? 0);
		const cardCount = anyPhotos ? own : CARDS_PER_STOP;
		if (cardCount > 0) {
			const centre = (cardCount - 1) / 2;
			Array.from({ length: cardCount }).forEach((_, card) => {
				const cardSpring = spring({ frame: markerFrame - card * cardStagger, fps, config: { mass: 1, stiffness: cardStiffness, damping: 16 } });
				const offsetFromCentre = card - centre;
				// Overshoot on the way in, settle at the fanned angle.
				const tilt = interpolate(cardSpring, [0, 1], [-24 * fanDirection, offsetFromCentre * 13 * fanDirection], { extrapolateRight: "clamp" });
				const spread = offsetFromCentre * 88 * fanDirection * cardSpring;
				// The middle card sits highest, so the fan arcs rather than sitting on a straight line.
				const lift = -132 * scale - (centre - Math.abs(offsetFromCentre)) * 14;
				const id = `cardPhoto-${index}-${card}`;
				safeSetLayout(id, "icon-size", 0.85 * scale * interpolate(cardSpring, [0, 1], [0.2, 1], { extrapolateRight: "clamp" }));
				safeSetLayout(id, "icon-rotate", tilt);
				safeSetPaint(id, "icon-translate", [spread * scale, lift]);
				safeSetOpacity(id, "icon-opacity", Math.min(fadeIn, cardSpring));
			});
		}
		safeSetLayout(`cardShadow-${index}`, "icon-size", 0.85 * scale * interpolate(driver, [0, 1], [0.4, 1], { extrapolateRight: "clamp" }));
		safeSetOpacity(`cardShadow-${index}`, "icon-opacity", cardCount > 0 ? fadeIn * 0.55 : 0);
	}
	safeSetLayout(`countText-${index}`, "text-size", countSize);

	//Animate pulse
	safeSetOpacity(`pulse-${index}`, "circle-opacity", pulseOpacity);
	safeSetPaint(`pulse-${index}`, "circle-radius", pulseRadius);

	//Animate destination name
	safeSetOpacity(`destinationName-${index}`, "text-opacity", fadeIn);
	safeSetOpacity(`destinationName-${index}`, "icon-opacity", fadeIn);
	safeSetPaint(`destinationName-${index}`, "icon-translate", [0, destinationNameTranslate]);
	safeSetPaint(`destinationName-${index}`, "text-translate", [0, destinationNameTranslate]);
};

/**
 * The same writes on the stop's LAST frame, which is what its layers hold from the moment the stop
 * is over: `markerDuration - 1`, the frame MarkerSequence ends on. FinishedSequences uses this for
 * every stop a tab started after.
 */
export const writeStopMarkerAtEnd = (map: any, frame: Omit<StopMarkerFrame, "markerFrame">, opts?: { opacities?: boolean }) =>
	writeStopMarker(map, { ...frame, markerFrame: frame.markerDuration - 1 }, opts);
